import { tileCenter, unitsToTile } from '../core/fixed';
import type { BuildingDef } from '../data/types';
import {
  isAdjacentToFootprint,
  isComplete,
  nearestFootprintTile,
  type Building,
} from '../entities/building';
import type { Player } from '../entities/player';
import type { Unit } from '../entities/unit';
import { inBounds, isBlocked, type GameMap } from '../map/gameMap';
import type { Pathfinder } from '../map/pathfinding';
import type { OccupancyFn } from '../map/unitGrid';
import { planPath } from './movement';

/** What the construction system needs from the simulation. `Sim` satisfies this. */
export interface ConstructionContext {
  readonly map: GameMap;
  readonly pathfinder: Pathfinder;
  player(owner: number): Player;
  getBuilding(id: number): Building | undefined;
  unitOnTile(tx: number, ty: number, includeHidden?: boolean): boolean;
  occupancyFor(unit: Unit): OccupancyFn;
}

export type PlacementRefusal = 'off-map' | 'blocked' | 'unit-in-way' | 'no-gold' | 'no-lumber';

/** Why `def` cannot be placed with its top-left at (x, y) for `owner`, or null when it can. */
export function placementRefusal(
  ctx: ConstructionContext,
  owner: number,
  def: BuildingDef,
  x: number,
  y: number,
): PlacementRefusal | null {
  for (let ty = y; ty < y + def.height; ty++) {
    for (let tx = x; tx < x + def.width; tx++) {
      if (!inBounds(ctx.map, tx, ty)) return 'off-map';
      if (isBlocked(ctx.map, tx, ty)) return 'blocked';
    }
  }
  for (let ty = y; ty < y + def.height; ty++) {
    for (let tx = x; tx < x + def.width; tx++) {
      if (ctx.unitOnTile(tx, ty, true)) return 'unit-in-way';
    }
  }
  const p = ctx.player(owner);
  if (p.gold < def.cost.gold) return 'no-gold';
  if (p.lumber < def.cost.lumber) return 'no-lumber';
  return null;
}

/** Send the unit to (continue to) build `site`. Returns false if it cannot. */
export function beginBuild(ctx: ConstructionContext, unit: Unit, site: Building): boolean {
  if (!unit.def.builds.includes(site.def.id) || site.owner !== unit.owner || isComplete(site))
    return false;
  unit.hidden = false;
  unit.state = { kind: 'building', buildingId: site.id, phase: 'toSite' };
  const t = nearestFootprintTile(site, unitsToTile(unit.x), unitsToTile(unit.y));
  unit.path = planPath(unit, ctx.map, ctx.pathfinder, tileCenter(t.x), tileCenter(t.y), {
    occupancy: ctx.occupancyFor(unit),
    accept: (tx, ty) => isAdjacentToFootprint(site, tx, ty),
  });
  return true;
}

/**
 * Runs after movement each tick for units in the 'building' state. Progress advances one
 * tick per builder standing beside the site, so two builders finish twice as fast.
 */
export function stepConstruction(ctx: ConstructionContext, unit: Unit): void {
  if (unit.state.kind !== 'building') return;
  const site = ctx.getBuilding(unit.state.buildingId);
  if (!site || isComplete(site)) {
    unit.state = { kind: 'idle' };
    return;
  }
  const adjacent = isAdjacentToFootprint(site, unitsToTile(unit.x), unitsToTile(unit.y));
  if (unit.state.phase === 'toSite') {
    if (unit.path) return;
    if (!adjacent) {
      unit.state = { kind: 'idle' }; // could not reach the site
      return;
    }
    unit.state = { ...unit.state, phase: 'constructing' };
    return;
  }
  if (!adjacent || !site.construction) {
    unit.state = { kind: 'idle' };
    return;
  }
  site.construction.ticksLeft--;
  if (site.construction.ticksLeft <= 0) {
    site.construction = null;
    unit.state = { kind: 'idle' };
  }
}
